/**
 * EXPERIENCE SYSTEM
 * 
 * This file contains the official implementation of the experience system.
 * All experience-related functions should be defined here.
 * 
 * DO NOT implement experience functions in other files. Import from here instead.
 * 
 * This centralizes all experience point logic and ensures consistent behavior
 * throughout the application.
 */

import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import confetti from 'canvas-confetti';

// Types
export interface ExperienceResult {
  success: boolean;
  userId?: string;
  previousExp?: number;
  currentExp?: number;
  level?: number;
  leveledUp?: boolean;
  expGained?: number;
  progressToNextLevel?: number;
  expForNextLevel?: number;
  error?: string;
}

type ExperienceUpdateListener = (userId: string, experience: number, level: number) => void;

// Internal state
const listeners: ExperienceUpdateListener[] = [];
let isInitialized = false;

// Add this type definition for the response from update_experience_direct
interface DirectExperienceUpdateResult {
  success: boolean;
  message?: string;
  before?: {
    experience: number;
    level: number;
  };
  after?: {
    experience: number;
    level: number;
    leveled_up: boolean;
  };
  amount?: number;
  error?: string;
  detail?: string;
}

/**
 * Initialize the experience system
 */
export function initExperienceSystem() {
  if (isInitialized) return;
  
  // Set up event listeners
  window.addEventListener('app:exp-updated', handleExperienceUpdated);
  
  // Add global methods to window for direct access
  window.updateExperienceReliably = updateExperienceReliably;

  console.log('[EXP SYSTEM] Experience system initialized');
  isInitialized = true;
}

/**
 * Clean up the experience system
 */
export function cleanupExperienceSystem() {
  window.removeEventListener('app:exp-updated', handleExperienceUpdated);
  listeners.length = 0;
  isInitialized = false;
}

/**
 * Handle the experience updated event
 */
function handleExperienceUpdated(event: CustomEvent) {
  try {
    const userId = event.detail?.userId;
    const experience = event.detail?.experience;
    const level = event.detail?.level;
    
    if (typeof userId === 'string' && typeof experience === 'number' && typeof level === 'number') {
      console.log(`[EXP SYSTEM] Experience updated: user=${userId}, exp=${experience}, level=${level}`);
      
      // Notify all listeners
      listeners.forEach(listener => {
        try {
          listener(userId, experience, level);
        } catch (listenerError) {
          console.error('[EXP SYSTEM] Error in experience update listener:', listenerError);
        }
      });
    }
  } catch (error) {
    console.error('[EXP SYSTEM] Error handling experience update event:', error);
  }
}

/**
 * Register a listener for experience updates
 * @returns A function to unregister the listener
 */
export function registerExperienceListener(listener: ExperienceUpdateListener): () => void {
  listeners.push(listener);
  return () => {
    const index = listeners.indexOf(listener);
    if (index !== -1) {
      listeners.splice(index, 1);
    }
  };
}

/**
 * Trigger an experience update event
 */
export function triggerExperienceUpdate(userId: string, experience: number, level: number) {
  console.log(`[EXP SYSTEM] Triggering experience update: user=${userId}, exp=${experience}, level=${level}`);
  window.dispatchEvent(new CustomEvent('app:exp-updated', { 
    detail: { userId, experience, level } 
  }));
}

/**
 * Add experience points to a user
 * This is the main function to use for awarding experience
 */
export async function addExperience(userId: string, amount: number): Promise<ExperienceResult> {
  console.log(`[EXP SYSTEM] Adding ${amount} XP to user ${userId}`);
  
  try {
    // Try to update using the reliable method first
    const success = await updateExperienceReliably(userId, amount);
    
    if (success) {
      console.log(`[EXP SYSTEM] Successfully added ${amount} XP to user ${userId} using reliable method`);
      // The event trigger is handled inside updateExperienceReliably
      return {
        success: true,
        userId,
        expGained: amount
      };
    }
    
    // Fall back to RPC method
    const { data, error } = await supabase.rpc('add_experience', {
      user_id: userId,
      exp_amount: amount
    });
    
    if (error) {
      console.error('[EXP SYSTEM] Error adding experience:', error);
      return {
        success: false,
        error: error.message
      };
    }
    
    // Type assertion to handle RPC result properly
    const rpcResult = data as unknown as {
      success: boolean;
      user_id: string;
      previous_exp: number;
      current_exp: number;
      level: number;
      leveled_up: boolean;
      exp_gained: number;
      progress_to_next_level: number;
      exp_for_next_level: number;
    };
    
    // Transform the response
    const result: ExperienceResult = {
      success: rpcResult.success,
      userId: rpcResult.user_id,
      previousExp: rpcResult.previous_exp,
      currentExp: rpcResult.current_exp,
      level: rpcResult.level,
      leveledUp: rpcResult.leveled_up,
      expGained: rpcResult.exp_gained,
      progressToNextLevel: rpcResult.progress_to_next_level,
      expForNextLevel: rpcResult.exp_for_next_level
    };
    
    // Show level up toast if applicable
    if (result.leveledUp) {
      toast({
        title: `Level Up!`,
        description: `Congratulations! You've reached level ${result.level}!`,
        variant: "default"
      });
    }
    
    // Trigger UI updates
    triggerExperienceUpdate(userId, result.currentExp!, result.level!);
    
    return result;
  } catch (error) {
    console.error('[EXP SYSTEM] Error in addExperience:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

/**
 * A reliable way to update experience that works even if the RPC fails
 */
export async function updateExperienceReliably(userId: string, amount: number): Promise<boolean> {
  console.log(`[EXP SYSTEM] Reliably adding ${amount} XP to user ${userId}`);
  
  try {
    // First try the RPC function
    console.log(`[EXP SYSTEM DEBUG] Attempting RPC call to add_experience for user ${userId}`);
    const { data, error } = await supabase.rpc('add_experience', {
      user_id: userId,
      exp_amount: amount
    });
    
    if (error) {
      console.error(`[EXP SYSTEM] RPC error:`, error);
      console.error(`[EXP SYSTEM DEBUG] RPC error details:`, JSON.stringify(error, null, 2));
      
      // Check for the specific badge_id ambiguous error
      if (error.message?.includes("column reference \"badge_id\" is ambiguous")) {
        console.warn("[EXP SYSTEM] Detected badge_id ambiguous error. Using direct update function as fallback.");
        
        // Log diagnostic information for developers
        try {
          const { data: diagnosticData, error: diagnosticError } = await supabase.rpc(
            'test_experience_update' as any, 
            { user_id: userId, amount: 1 }
          );
          
          if (diagnosticError) {
            console.error('[EXP SYSTEM] Diagnostic test failed:', diagnosticError);
          } else if (diagnosticData) {
            console.log('[EXP SYSTEM] Diagnostic test result:', diagnosticData);
          }
        } catch (diagnosticErr) {
          console.error('[EXP SYSTEM] Error running diagnostic:', diagnosticErr);
        }
      }
      
      // Try the new direct update function first
      console.log(`[EXP SYSTEM] Trying direct update function...`);
      
      try {
        // Use a generic type to avoid TypeScript errors
        const { data: directData, error: directError } = await supabase.rpc(
          'update_experience_direct' as any, 
          {
            user_id: userId,
            amount: amount
          }
        );
        
        if (directError) {
          console.error(`[EXP SYSTEM] Direct update error:`, directError);
          console.error(`[EXP SYSTEM DEBUG] Direct update error details:`, JSON.stringify(directError, null, 2));
        } else {
          console.log(`[EXP SYSTEM] Direct update success:`, directData);
          
          // Type cast the result to match our interface
          const result = directData as unknown as DirectExperienceUpdateResult;
          
          // Extract data from the result
          if (result.success && result.after) {
            // Show level up notification if applicable
            if (result.after.leveled_up) {
              const newLevel = result.after.level;
              toast({
                title: `Level Up!`,
                description: `Congratulations! You've reached level ${newLevel}!`,
                variant: "default"
              });
            }
            
            // Trigger UI updates
            triggerExperienceUpdate(
              userId, 
              result.after.experience,
              result.after.level
            );
            
            return true;
          }
        }
      } catch (directUpdateError) {
        console.error(`[EXP SYSTEM] Error using direct update:`, directUpdateError);
      }
      
      // Fall back to manual update as last resort
      try {
        console.log(`[EXP SYSTEM] Falling back to manual update`);
        
        // Get current profile
        console.log(`[EXP SYSTEM DEBUG] Fetching current profile for user ${userId}`);
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('experience, level')
          .eq('id', userId)
          .single();
          
        if (profileError) {
          console.error(`[EXP SYSTEM] Profile fetch error:`, profileError);
          console.error(`[EXP SYSTEM DEBUG] Profile fetch error details:`, JSON.stringify(profileError, null, 2));
          return false;
        }
        
        // Calculate new values
        const currentExp = profile.experience || 0;
        const currentLevel = profile.level || 1;
        const newExp = currentExp + amount;
        let newLevel = currentLevel;
        
        // Check for level up
        while (newExp >= newLevel * 100) {
          newLevel++;
        }
        
        // Update profile
        console.log(`[EXP SYSTEM DEBUG] Updating profile directly. Current XP: ${currentExp}, New XP: ${newExp}, Level: ${currentLevel} -> ${newLevel}`);
        const { error: updateError } = await supabase
          .from('profiles')
          .update({
            experience: newExp,
            level: newLevel,
            updated_at: new Date().toISOString()
          })
          .eq('id', userId);
          
        if (updateError) {
          console.error(`[EXP SYSTEM] Update error:`, updateError);
          console.error(`[EXP SYSTEM DEBUG] Update error details:`, JSON.stringify(updateError, null, 2));
          return false;
        }
        
        // Show level up notification if applicable
        if (newLevel > currentLevel) {
          toast({
            title: `Level Up!`,
            description: `Congratulations! You've reached level ${newLevel}!`,
            variant: "default"
          });
        }
        
        // Trigger UI updates
        triggerExperienceUpdate(userId, newExp, newLevel);
        
        return true;
      } catch (fallbackError) {
        console.error(`[EXP SYSTEM] Fallback error:`, fallbackError);
        console.error(`[EXP SYSTEM DEBUG] Fallback error details:`, JSON.stringify(fallbackError, null, 2));
        return false;
      }
    }
    
    console.log(`[EXP SYSTEM] RPC success:`, data);
    console.log(`[EXP SYSTEM DEBUG] RPC result details:`, JSON.stringify(data, null, 2));
    
    // Type assertion for the RPC result
    const rpcResult = data as unknown as {
      current_exp: number;
      level: number;
    };
    
    // Trigger UI updates
    triggerExperienceUpdate(
      userId, 
      rpcResult.current_exp, 
      rpcResult.level
    );
    
    return true;
  } catch (e) {
    console.error(`[EXP SYSTEM] Unexpected error:`, e);
    console.error(`[EXP SYSTEM DEBUG] Error type:`, e instanceof Error ? e.constructor.name : typeof e);
    return false;
  }
}

/**
 * Display a level-up toast with confetti animation
 * @param level - The new level achieved
 */
export function showLevelUpToast(level: number) {
  // Create toast with high priority and level-up marker
  toast({
    title: "🎉 Level Up!",
    description: `Congratulations! You've reached level ${level}!`,
    variant: "default",
    duration: 5000, // Show for 5 seconds
    isLevelUp: true, // Mark as level-up toast for priority system
  });
  
  // Fire confetti
  triggerLevelUpConfetti();
}

/**
 * Trigger a celebratory confetti animation
 */
export function triggerLevelUpConfetti() {
  const duration = 2000;
  const end = Date.now() + duration;
  
  const colors = ['#FF6E50', '#9D4EDD', '#FFD700', '#00BFFF'];
  
  (function frame() {
    confetti({
      particleCount: 40,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.8 },
      colors: colors,
    });
    
    confetti({
      particleCount: 40,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.8 },
      colors: colors,
    });
    
    if (Date.now() < end) {
      requestAnimationFrame(frame);
    }
  }());
}

/**
 * Check for notifications related to level-ups and show a toast if needed
 * @param notifications - Array of notifications to check
 */
export function checkForLevelUpNotifications(notifications: any[]) {
  if (!notifications || !notifications.length) return;
  
  // Look for unread achievement notifications related to level-ups
  const levelUpNotification = notifications.find(
    notification => 
      notification.type === 'achievement' && 
      notification.entity_type === 'level' && 
      !notification.is_read
  );
  
  if (levelUpNotification) {
    // Extract level from notification content (e.g., "Congratulations! You reached level 5!")
    const levelMatch = levelUpNotification.content.match(/level (\d+)/i);
    if (levelMatch && levelMatch[1]) {
      const level = parseInt(levelMatch[1], 10);
      
      if (!isNaN(level)) {
        // Show toast with confetti
        showLevelUpToast(level);
      }
    }
  }
}

// For backward compatibility
export const awardExperience = addExperience;

// Specialized experience functions for specific actions

/**
 * Award experience for creating a post
 */
export async function awardPostCreationExperience(userId: string): Promise<ExperienceResult> {
  console.log(`Awarding post creation experience to user ${userId}`);
  return addExperience(userId, 10); // 10 XP for creating a post
}

/**
 * Award experience for receiving a like
 */
export async function awardLikeReceivedExperience(postOwnerId: string): Promise<ExperienceResult> {
  console.log(`Awarding like received experience to user ${postOwnerId}`);
  return addExperience(postOwnerId, 2); // 2 XP for receiving a like
}

/**
 * Award experience for receiving an upvote
 */
export async function awardUpvoteReceivedExperience(postOwnerId: string): Promise<ExperienceResult> {
  console.log(`Awarding upvote received experience to user ${postOwnerId}`);
  return addExperience(postOwnerId, 3); // 3 XP for receiving an upvote
}

/**
 * Award experience for receiving a comment
 */
export async function awardCommentReceivedExperience(postOwnerId: string): Promise<ExperienceResult> {
  console.log(`Awarding comment received experience to user ${postOwnerId}`);
  return addExperience(postOwnerId, 5); // 5 XP for receiving a comment
}

/**
 * Award daily login experience
 */
export async function awardDailyLoginExperience(userId: string): Promise<ExperienceResult> {
  console.log(`Awarding daily login experience to user ${userId}`);
  return addExperience(userId, 5); // 5 XP for daily login
}

// Declare global augmentations for TypeScript
declare global {
  interface Window {
    updateExperienceReliably: (userId: string, amount: number) => Promise<boolean>;
    lastProfileRefreshTime?: number;
    forceProfileRefresh?: (userId: string) => Promise<void> | void;
  }
} 